import { useCallback, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { useToast } from '../ui/Toasts/use-toast';
import { Image, Mail, Share } from 'lucide-react';
import { BsLinkedin } from 'react-icons/bs';
import { FaBookmark, FaFacebook } from 'react-icons/fa';
import { IoIosLink } from 'react-icons/io';
import { RiTwitterXFill } from 'react-icons/ri';
import posthog from 'posthog-js';

interface ShareButtonProps {
  className?: string;
  articleTitle?: string;
  articleUrl?: string;
}

// Analytics tracking function
const trackShareEvent = (platform: string, articleTitle?: string) => {
  posthog.capture('article_shared', {
    platform: platform.toLowerCase().replaceAll(' ', '_'),
    has_custom_title: Boolean(articleTitle)
  });

  // Track with Vercel Analytics
  if (typeof window !== 'undefined') {
    const windowWithVa = window as unknown as {
      va?: (event: string, data: string, metadata: object) => void;
    };
    if (windowWithVa.va) {
      windowWithVa.va('track', 'Share', {
        platform,
        article: articleTitle || document.title,
        url: window.location.href
      });
    }
  }
};

const createShareActions = (articleTitle?: string, articleUrl?: string) => ({
  copyLink: {
    icon: IoIosLink,
    label: 'Copy Link',
    action: async () => {
      const url = articleUrl || window.location.href;
      await navigator.clipboard.writeText(url);
      trackShareEvent('Copy Link', articleTitle);
      return 'Bioinfometrics blog link copied!';
    }
  },
  sendEmail: {
    icon: Mail,
    label: 'Send as an email',
    action: () => {
      const url = articleUrl || window.location.href;
      const title = articleTitle || document.title;
      const emailSubject = encodeURIComponent(`Must-Read: ${title}`);
      const emailBody = encodeURIComponent(
        `Hi there,

I found this Bioinfometrics article really insightful and thought you might enjoy it too: "${title}"

Check it out here: ${url}

Best regards.`
      );
      window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
      trackShareEvent('Email', articleTitle);
      return 'Launching your email client...';
    }
  },
  getImages: {
    icon: Image,
    label: 'Shareable Images',
    action: async () => {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      trackShareEvent('Generate Images', articleTitle);
      return 'Images for sharing are ready!';
    }
  },
  shareToNotes: {
    icon: FaBookmark,
    label: 'Save to Notes',
    action: () => {
      trackShareEvent('Save to Notes', articleTitle);
      return 'Blog saved to your notes successfully!';
    }
  },
  shareToFacebook: {
    icon: FaFacebook,
    label: 'Share on Facebook',
    action: () => {
      const url = articleUrl || window.location.href;
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        '_blank'
      );
      trackShareEvent('Facebook', articleTitle);
      return 'Opening Facebook share dialog...';
    }
  },
  shareToLinkedIn: {
    icon: BsLinkedin,
    label: 'Share on LinkedIn',
    action: () => {
      const url = articleUrl || window.location.href;
      const title = articleTitle || document.title;
      window.open(
        `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
        '_blank'
      );
      trackShareEvent('LinkedIn', articleTitle);
      return 'Opening LinkedIn share dialog...';
    }
  },
  shareToX: {
    icon: RiTwitterXFill,
    label: 'Tweet This Blog',
    action: () => {
      const url = articleUrl || window.location.href;
      const title = articleTitle || 'Bioinfometrics Blog';
      const text = encodeURIComponent(`Check out this insightful ${title}!`);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`,
        '_blank',
        'width=550,height=420'
      );
      trackShareEvent('Twitter/X', articleTitle);
      return 'Opening Twitter share dialog...';
    }
  }
});

export const ShareButton: React.FC<ShareButtonProps> = ({
  className,
  articleTitle,
  articleUrl
}) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const shareActions = createShareActions(articleTitle, articleUrl);

  const handleShare = useCallback(
    async (action: () => string | Promise<string>) => {
      try {
        const message = await action();
        toast.success(message);
      } catch (error) {
        posthog.captureException(error);
        console.error('Share error:', error);
        toast.error('Sharing Failed', {
          description: 'An error occurred while trying to share.'
        });
      }
    },
    [toast]
  );

  return (
    <div className={`relative ${className || ''}`}>
      <DropdownMenu
        open={open}
        onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className='hover:bg-muted flex size-9 items-center justify-center rounded-full border transition-colors'
            aria-label='Share article'>
            <Share className='size-4' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='border-border/40 bg-card/95 z-[100] mt-1 w-56 rounded-xl p-1.5 shadow-xl backdrop-blur-md'>
          {Object.entries(shareActions).map(
            ([key, { icon: Icon, label, action }]) => (
              <DropdownMenuItem
                key={key}
                className='focus:bg-primary/10 focus:text-primary flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm transition-all'
                onSelect={() => handleShare(action)}>
                <Icon
                  className={`size-4 shrink-0 transition-colors ${
                    key === 'shareToNotes'
                      ? 'text-brand-accent'
                      : key === 'shareToFacebook'
                        ? 'text-[#0966ff]'
                        : key === 'shareToLinkedIn'
                          ? 'text-[#0966c2]'
                          : key === 'shareToX'
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                  }`}
                />
                {label}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
